// Входы генерации в сообщении: по готовому медиафайлу видно, из чего он сделан —
// кадры с их ролями («Первый кадр» / «Последний кадр»), элементы библиотеки,
// референсы. Раньше история была беднее бара: картинки шли одним пулом без
// ролей, элементы — отдельным блоком, а остальных каналов не было вовсе.
//
// Состав каналов и их заголовки берутся из общего описания (`entities/chat`),
// того же, которым живёт ряд входов в баре.

import { useState } from "react";
import { ChevronDown, Eye, Images, Music, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { inputChannelTitleKey } from "~/entities/chat";
import { ElementDetailsDialog, toSnapshotDetailsView } from "~/entities/element";
import { cn } from "~/shared/lib/utils";
import { MediaLightbox } from "~/shared/ui/media-lightbox";
import { MediaThumb } from "~/shared/ui/media-thumb";

import { buildInputsLightboxFeed } from "./message-inputs-lightbox";

import type { MessageInputChannelView, MessageInputItem } from "~/entities/chat";
import type { ArtifactType } from "~/shared/data/chat-models";

interface MessageInputsProps {
    channels: readonly MessageInputChannelView[];
    /** Что порождала генерация — от этого зависит подпись канала кадров. */
    artifactType?: ArtifactType | null;
}

export function MessageInputs({ channels, artifactType }: MessageInputsProps) {
    // `elements` рядом с `chat`: карточка входа подписана ключами библиотеки.
    const { t } = useTranslation(["chat", "elements"]);
    const [open, setOpen] = useState(true);
    // Открыт ли просмотр и на каком месте СКВОЗНОЙ ленты входов он стоит. Лента
    // одна на всё сообщение: человек смотрит, что реально ушло в генерацию, и
    // обязан пролистать входы стрелками. Прежде открывался ровно один вход
    // («1 / 1»), и увидеть остальные можно было только закрыв и ткнув в соседний.
    const [lightboxAt, setLightboxAt] = useState<number | null>(null);
    // Карточка входа — по снимку запроса, а не по текущей библиотеке: элемент
    // могли переименовать или удалить, сообщение обязано показывать ушедшее.
    const [details, setDetails] = useState<MessageInputItem["snapshot"] | null>(null);

    if (channels.length === 0) return null;

    const total = channels.reduce((sum, channel) => sum + channel.items.length, 0);

    // Лента одна на всё сообщение, плитка открывает её на своём первом кадре.
    const { slides, startAt } = buildInputsLightboxFeed(channels);

    return (
        <div className="mb-2">
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                className="mb-1 flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
            >
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open ? "" : "-rotate-90")} />
                {t("message.inputs", { count: total })}
            </button>

            {open && (
                <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
                    {channels.map((channel, channelIndex) => (
                        <div key={channel.channel} className="flex flex-col gap-1.5">
                            <span className="text-[11px] font-medium tracking-[0.12em] text-zinc-500 uppercase">
                                {t(inputChannelTitleKey(channel.channel, artifactType))}
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {channel.items.map((item, index) => (
                                    <div
                                        key={`${item.url ?? "empty"}-${String(index)}`}
                                        className="relative flex w-16 flex-col gap-1"
                                    >
                                        {/* Карточка — отдельной кнопкой: клик по
                                            плитке открывает картинки, и совмещать
                                            с ним просмотр полей нельзя. */}
                                        {item.snapshot && (
                                            <button
                                                type="button"
                                                onClick={() => setDetails(item.snapshot ?? null)}
                                                aria-label={t("elements:actions.view")}
                                                title={t("elements:actions.view")}
                                                className="absolute top-0.5 right-0.5 z-10 rounded bg-black/60 p-1 text-white backdrop-blur transition-colors hover:bg-black/80"
                                            >
                                                <Eye className="size-3" aria-hidden />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={item.lightbox.length === 0}
                                            onClick={() => setLightboxAt(startAt[channelIndex]?.[index] ?? 0)}
                                            title={item.title ?? item.label ?? undefined}
                                            aria-label={
                                                item.label
                                                    ? t("message.openInput", { name: item.label })
                                                    : t("message.openInputUnnamed")
                                            }
                                            className={cn(
                                                "relative h-16 w-16 overflow-hidden rounded-md border border-white/5 bg-zinc-900",
                                                item.lightbox.length === 0
                                                    ? "cursor-default"
                                                    : "cursor-zoom-in hover:border-white/25",
                                            )}
                                        >
                                            {item.url && item.kind === "audio" ? (
                                                // У звука нет кадра: иконка со значком
                                                // воспроизведения, плеер — в лайтбоксе.
                                                <span className="absolute inset-0 flex items-center justify-center text-violet-300">
                                                    <Music className="h-5 w-5" aria-hidden />
                                                    <span className="absolute right-1 bottom-1 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white">
                                                        <Play
                                                            className="h-2.5 w-2.5 translate-x-px"
                                                            aria-hidden
                                                        />
                                                    </span>
                                                </span>
                                            ) : item.url ? (
                                                <MediaThumb
                                                    url={item.url}
                                                    kind={item.kind ?? "image"}
                                                    className="absolute inset-0"
                                                />
                                            ) : (
                                                <span className="absolute inset-0 flex items-center justify-center text-zinc-500">
                                                    <Images className="h-5 w-5" aria-hidden />
                                                </span>
                                            )}
                                        </button>
                                        {/* Строку подписи резервирует каждая плитка — иначе
                                            колонка без ролей съезжает вверх относительно соседней. */}
                                        <span className="line-clamp-1 h-4 text-center text-[11px] text-zinc-400">
                                            {item.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <MediaLightbox
                items={slides}
                initialIndex={lightboxAt ?? 0}
                isOpen={lightboxAt !== null}
                onClose={() => setLightboxAt(null)}
                showShare
            />

            <ElementDetailsDialog
                view={details ? toSnapshotDetailsView(t, details) : null}
                open={details !== null}
                onOpenChange={(next) => {
                    if (!next) setDetails(null);
                }}
            />
        </div>
    );
}
